import React, { useState } from 'react';
import { Select, DatePicker, Slider, Checkbox, Form, Input } from 'antd';
import { ButtonCommon, SelectCommon } from 'components/common';
import { CaretDown, CalendarBlank, Funnel, XCircle } from '@phosphor-icons/react';
import { PurchaseOrderFilter, PurchaseOrderStatusCode, QueryParams } from 'types';
import { formatPrice } from 'utils';
import { Staff, Supplier, Warehouse } from 'models';
import { useGetAllWarehousesQuery, useGetSuppliersQuery, useGetStaffsQuery } from 'services';

interface FilterPurchaseProps {
  initialFilter?: PurchaseOrderFilter;
  onApplyFilter: (filter: PurchaseOrderFilter) => void;
  onClearFilter: () => void;
}

const FilterPurchase: React.FC<FilterPurchaseProps> = ({ initialFilter, onApplyFilter, onClearFilter }) => {
  const [form] = Form.useForm();
  const [priceRange, setPriceRange] = useState<[number, number]>([0, 150000000]);
  const supplierQueryParams: QueryParams = {
    size: 100,
    page: 1,
    sort: 'createdAt:asc',
    include: 'baseUser',
  } as unknown as QueryParams;
  const warehouseQueryParams: QueryParams = {
    size: 100,
    page: 1,
    sort: 'createdAt:asc',
  } as unknown as QueryParams;
  const staffQueryParams: QueryParams = {
    size: 100,
    page: 1,
    sort: 'createdAt:asc',
    include: 'baseUser',
  } as unknown as QueryParams;
  const { data: warehousesData } = useGetAllWarehousesQuery({
    query: warehouseQueryParams,
  });
  const { data: suppliersData } = useGetSuppliersQuery({
    query: supplierQueryParams,
  });
  const { data: staffsData } = useGetStaffsQuery({
    query: staffQueryParams,
  });
  const suppliers: Supplier[] = suppliersData?.data?.rows || [];
  const warehouses: Warehouse[] = warehousesData?.data?.rows || [];
  const staffs: Staff[] = staffsData?.data?.rows || [];
  const handleClearFilter = () => {
    form.resetFields();
    setPriceRange(priceRange);
    form.setFieldsValue({
      supplierId: undefined,
      warehouseId: undefined,
      code: undefined,
      deliveryDate: initialFilter?.deliveryDate || null,
      status: initialFilter?.status || undefined,
      createdById: undefined,
      hideExpired: false,
      hideCompleted: false || initialFilter?.hideCompleted,
      codeSearch: undefined,
    });
    onClearFilter();
  };

  const handleApplyFilter = () => {
    const values = form.getFieldsValue();
    onApplyFilter({
      ...values,
      priceRange,
    });
  };

  return (
    <div className='bg-white rounded-lg overflow-hidden h-[820px] w-full '>
      <div className='flex justify-between items-center px-6 py-3'>
        <h2 className='text-xl font-bold text-[var(--textBlack100)]'>LỌC PHIẾU NHẬP HÀNG</h2>
      </div>

      <div className='px-6 py-3'>
        <Form form={form} layout='vertical' initialValues={initialFilter} className='filter-form'>
          <div className='flex flex-col'>
            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>Nhà cung cấp</span>
              }
              name='supplierId'
            >
              <SelectCommon
                placeholder='Chọn nhà cung cấp'
                className='w-full'
                suffixIcon={<CaretDown color='black' size={18} />}
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                sizeSelect='sm'
                selectType='filterSelect'
                options={suppliers.map((supplier: Supplier) => ({
                  value: supplier.id,
                  label: supplier?.baseUser?.name,
                }))}
              />
            </Form.Item>

            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>Kho nhập</span>
              }
              name='warehouseId'
            >
              <SelectCommon
                placeholder='Chọn kho'
                className='w-full'
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                sizeSelect='sm'
                selectType='filterSelect'
                suffixIcon={<CaretDown color='black' size={18} />}
                options={warehouses.map((warehouse: Warehouse) => ({
                  value: warehouse.id,
                  label: warehouse.name,
                }))}
              />
            </Form.Item>

            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>Mã phiếu nhập</span>
              }
              name='codeSearch'
            >
              <Input
                placeholder='Nhập mã phiếu nhập'
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                className='w-full'
              />
            </Form.Item>

            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                  Ngày nhập hàng
                </span>
              }
              name='deliveryDate'
            >
              <DatePicker
                className='w-full'
                placeholder='Chọn ngày nhập hàng'
                format='DD/MM/YYYY'
                suffixIcon={<CalendarBlank className='text-[var(--textBlack100)]' />}
              />
            </Form.Item>

            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                  Trạng thái phiếu nhập
                </span>
              }
              name='status'
            >
              <Select
                placeholder='Tất cả trạng thái'
                className='w-full'
                suffixIcon={<CaretDown color='black' size={18} />}
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                options={[
                  { value: '', label: 'Tất cả trạng thái' },
                  { value: PurchaseOrderStatusCode.READY, label: 'Sẵn sàng nhập hàng' },
                  { value: PurchaseOrderStatusCode.INPROGRESS, label: 'Đang nhập hàng' },
                  { value: PurchaseOrderStatusCode.CANCELLED, label: 'Phiếu nhập đã hủy' },
                  { value: PurchaseOrderStatusCode.RECEIVED, label: 'Đã nhập hàng' },
                  { value: PurchaseOrderStatusCode.DELAYED, label: 'Trễ hạn nhập hàng' },
                ]}
              />
            </Form.Item>

            <div className='mb-[24px]'>
              <p className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)] mb-2'>Khoảng giá</p>
              <Slider
                range
                min={0}
                max={150000000}
                value={priceRange}
                onChange={(value) => setPriceRange(value as [number, number])}
                className=' my-2'
                trackStyle={[{ backgroundColor: 'var(--primaryColor500)' }]}
                handleStyle={[
                  {
                    borderColor: 'var(--primaryColor500)',
                    backgroundColor: 'var(--primaryColor500)',
                    boxShadow: '0px 2px 5px rgba(38, 51, 77, 0.03)',
                  },
                  {
                    borderColor: 'var(--primaryColor500)',
                    backgroundColor: 'var(--primaryColor500)',
                    boxShadow: '0px 2px 5px rgba(38, 51, 77, 0.03)',
                  },
                ]}
                railStyle={{ backgroundColor: 'var(--primaryColor50)' }}
              />
              <div className='flex justify-between'>
                <div>
                  <span className='text-[12px] font-medium text-[var(--primaryColor500)]'>
                    {formatPrice(priceRange[0])}
                  </span>
                  <span className='text-[12px] font-medium text-[var(--primaryColor300)] ml-1'>VND</span>
                </div>
                <div>
                  <span className='text-[12px] font-medium text-[var(--primaryColor500)]'>
                    {formatPrice(priceRange[1])}
                  </span>
                  <span className='text-[12px] font-medium text-[var(--primaryColor300)] ml-1'>VND</span>
                </div>
              </div>
            </div>

            <Form.Item
              label={
                <span className='text-[14px] leading-[20px] font-medium text-[var(--textBlack100)]'>
                  Nhân viên nhập hàng
                </span>
              }
              name='createdById'
            >
              <SelectCommon
                placeholder='Chọn nhân viên nhập hàng'
                className='w-full'
                allowClear={{
                  clearIcon: <XCircle color='black' weight='fill' size={18} className='!-mt-0.5 !-ml-1.5' />,
                }}
                sizeSelect='sm'
                selectType='filterSelect'
                suffixIcon={<CaretDown color='black' size={18} />}
                options={staffs.map((staff) => ({
                  value: staff.id,
                  label: staff.baseUser?.name,
                }))}
              />
            </Form.Item>

            <Form.Item name='hideExpired' valuePropName='checked'>
              <Checkbox>Ẩn phiếu quá hạn</Checkbox>
            </Form.Item>

            <Form.Item name='hideCompleted' valuePropName='checked' className='mb-6'>
              <Checkbox>Ẩn phiếu đã nhập</Checkbox>
            </Form.Item>
          </div>
        </Form>

        <div className='flex gap-6'>
          <ButtonCommon
            color='none'
            size='sm'
            radius='rounded'
            className='min-w-[112px] hover:font-normal'
            onClick={handleClearFilter}
          >
            Xóa bộ lọc
          </ButtonCommon>
          <ButtonCommon
            color='primary'
            size='sm'
            radius='rounded'
            fullWidth
            className='flex justify-center items-center'
            onClick={handleApplyFilter}
            icon={<Funnel size={16} />}
            iconPosition='right'
          >
            Áp dụng bộ lọc
          </ButtonCommon>
        </div>
      </div>
    </div>
  );
};

export default FilterPurchase;
